// v0.4.45 — Submission modal. Flips an amendment OPEN → SUBMITTED.
// Backend enforces: corrective_calc_id must exist AND be frozen.
//
// v0.6.12 A5-27 — pick, don't write.
//  - The corrective calculation is chosen from the finalised ones
//    (FiledCalcSelect), not typed as an id.
//  - The "corrective filing reference" is no longer typed at all. The
//    box's own placeholder was the app's filing_reference format — the
//    reference routes_freeze.py stamps on the calculation at freeze — and
//    nothing compared what was typed with the calculation's own. It is
//    now read from the chosen calculation and shown read-only. The
//    authority's receipt number is a different thing and is recorded in
//    SubmissionRecordPanel.
//  - The reason is chosen from AMENDMENT_SUBMISSION_REASONS, with the
//    counter and textarea shown only for "Other".
import React, { useMemo, useState } from "react";
import { recordAmendmentSubmission } from "../lib/api";
import { FiledCalcSelect, correctiveCalcFilter } from "./FiledCalcSelect";
import { PresetReasonSelect } from "./PresetReasonSelect";
import { AMENDMENT_SUBMISSION_REASONS } from "../lib/reviewReasonPresets";
import { useTranslation } from "react-i18next";

const REASON_MIN = 50;

const counterColour = (n) => {
  if (n >= REASON_MIN) return "text-emerald-700 dark:text-emerald-300";
  if (n >= 30) return "text-amber-600 dark:text-amber-400";
  return "text-rose-500 dark:text-rose-400";
};

export const AmendmentSubmissionModal = ({ amendment, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [calcId, setCalcId] = useState("");
  const [filingRef, setFilingRef] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const trimmedReason = reason.trim();
  const canSubmit = useMemo(() => (
    !submitting
      && calcId.trim().length > 0
      && filingRef.trim().length > 0
      && trimmedReason.length >= REASON_MIN
  ), [calcId, filingRef, trimmedReason, submitting]);

  const submit = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await recordAmendmentSubmission(amendment.id, {
        corrective_calc_id: calcId.trim(),
        corrective_filing_ref: filingRef.trim(),
        reason: trimmedReason,
      });
      onSubmitted?.(res);
      onClose?.();
    } catch (e) {
      setServerError(
        e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || e?.message
          || "Submission failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="amendment-submission-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-2xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700/90">
              {t("ui.amendments.submission_title", { defaultValue: "Record corrective submission" })}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300 truncate">
              {amendment.id}
            </div>
          </div>
          <button
            data-testid="amendment-submission-close"
            onClick={onClose}
            className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 px-2"
            aria-label={t("ui.common.close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300 block mb-1">
              {t("ui.amendments.corrected_filing", { defaultValue: "Corrected filing" })}
            </label>
            <FiledCalcSelect
              testid="submission-corrective-calc-id"
              value={calcId}
              onChange={(id, calc) => {
                setCalcId(id);
                setFilingRef(calc?.filing_reference || "");
              }}
              filter={correctiveCalcFilter(amendment)}
              className="text-sm py-2"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {t("ui.amendments.only_finalised", { defaultValue: "Only finalised calculations for this country and client that have not been replaced by a later version are listed." })}
            </p>
          </div>

          <div>
            <label className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300 block mb-1">
              {t("ui.amendments.corrective_ref", { defaultValue: "Corrective filing reference" })}
            </label>
            <input
              data-testid="submission-filing-ref"
              value={filingRef}
              readOnly
              aria-readonly="true"
              placeholder={t("ui.amendments.filing_ref_placeholder", { defaultValue: "Filled in from the corrected filing" })}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-700 dark:text-slate-200 text-sm tabular-nums cursor-default focus:outline-none"
            />
          </div>

          <div>
            <label className="text-[10px] tracking-normal text-slate-600 dark:text-slate-300 block mb-1">
              {t("ui.amendments.reason", { defaultValue: "Reason" })}
            </label>
            <PresetReasonSelect
              testid="submission-reason-preset"
              presets={AMENDMENT_SUBMISSION_REASONS}
              preset={preset}
              onPick={(p, v) => { setPreset(p); setReason(v); }}
              className="text-sm py-2"
            />
          </div>
        </div>

        {serverError && (
          <div
            data-testid="submission-server-error"
            className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 rounded-lg px-3 py-2 mt-3"
          >
            {serverError}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="submission-cancel"
            onClick={onClose}
            className="border border-slate-300 dark:border-slate-700 hover:border-slate-500 dark:hover:border-slate-400 text-slate-500 dark:text-slate-400 px-4 py-2 rounded-full text-sm"
          >
            {t("ui.amendments.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            data-testid="submission-submit"
            onClick={submit}
            disabled={!canSubmit}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              canSubmit
                ? "bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed"
            }`}
          >
            {submitting ? t("ui.amendments.submitting", { defaultValue: "Submitting…" }) : t("ui.amendments.record_submission", { defaultValue: "Record submission" })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmendmentSubmissionModal;
